<!-- Overall Status Banner -->
@if (isLoadingHealth()) {
  <div class="grid grid-cols-2 gap-4 md:grid-cols-4 mb-6">
    @for (_ of [1,2,3,4]; track $index) {
      <div hlmCard class="p-4">
        <hlm-skeleton class="h-4 w-24 mb-3" />
        <hlm-skeleton class="h-8 w-16 mb-2" />
        <hlm-skeleton class="h-3 w-32" />
      </div>
    }
  </div>
} @else {
  <!-- Overall Status Alert -->
  <div
    class="mb-6 flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium ring-1"
    [class.bg-green-50]="overallStatus() === 'healthy'"
    [class.ring-green-200]="overallStatus() === 'healthy'"
    [class.text-green-800]="overallStatus() === 'healthy'"
    [class.bg-amber-50]="overallStatus() === 'degraded'"
    [class.ring-amber-200]="overallStatus() === 'degraded'"
    [class.text-amber-800]="overallStatus() === 'degraded'"
    [class.bg-red-50]="overallStatus() === 'unhealthy'"
    [class.ring-red-200]="overallStatus() === 'unhealthy'"
    [class.text-red-800]="overallStatus() === 'unhealthy'">
    @if (overallStatus() === 'healthy') {
      <svg class="h-5 w-5 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
    } @else if (overallStatus() === 'degraded') {
      <svg class="h-5 w-5 text-amber-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
    } @else {
      <svg class="h-5 w-5 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
    }
    <span>{{ getOverallStatusLabel(overallStatus()) }}</span>
    <span class="ms-auto text-xs opacity-70">Last updated: {{ lastUpdated() | date:'HH:mm:ss' }}</span>
  </div>

  <!-- Health Check Cards Row -->
  <div class="grid grid-cols-2 gap-4 md:grid-cols-4 mb-6">
    <!-- API Status -->
    <div hlmCard class="p-4">
      <div class="flex items-start justify-between mb-2">
        <p class="text-xs font-medium text-muted-foreground uppercase tracking-wide">API Status</p>
        <span hlmBadge [variant]="isSimpleHealthy() ? 'default' : 'destructive'">
          {{ isSimpleHealthy() ? 'Up' : 'Down' }}
        </span>
      </div>
      <p class="text-lg font-semibold">{{ simpleHealth()?.data?.status ?? 'Unknown' }}</p>
      <p class="text-xs text-muted-foreground mt-1">{{ simpleHealth()?.status?.responseTime ?? 'N/A' }}ms response</p>
      @if (simpleHealth()?.data?.message) {
        <p class="text-xs text-muted-foreground mt-1 truncate">{{ simpleHealth()!.data!.message }}</p>
      }
    </div>

    <!-- System Health -->
    <div hlmCard class="p-4">
      <div class="flex items-start justify-between mb-2">
        <p class="text-xs font-medium text-muted-foreground uppercase tracking-wide">System</p>
        <span hlmBadge [variant]="isFullHealthy() ? 'default' : 'destructive'">
          {{ isFullHealthy() ? 'Healthy' : 'Issues' }}
        </span>
      </div>
      <p class="text-lg font-semibold">{{ fullHealth()?.data?.status ?? 'Unknown' }}</p>
      <p class="text-xs text-muted-foreground mt-1">{{ fullHealth()?.data?.environment ?? 'N/A' }}</p>
    </div>

    <!-- Database -->
    <div hlmCard class="p-4">
      <div class="flex items-start justify-between mb-2">
        <p class="text-xs font-medium text-muted-foreground uppercase tracking-wide">Database</p>
        <span hlmBadge [variant]="isDatabaseHealthy() ? 'default' : 'destructive'">
          {{ isDatabaseHealthy() ? 'Connected' : 'Error' }}
        </span>
      </div>
      <p class="text-lg font-semibold">{{ databaseHealth()?.data?.status ?? 'Unknown' }}</p>
      <p class="text-xs text-muted-foreground mt-1">{{ databaseHealth()?.status?.responseTime ?? 'N/A' }}ms</p>
    </div>

    <!-- Version -->
    <div hlmCard class="p-4">
      <div class="flex items-start justify-between mb-2">
        <p class="text-xs font-medium text-muted-foreground uppercase tracking-wide">Version</p>
        <span hlmBadge variant="outline">Info</span>
      </div>
      <p class="text-lg font-semibold">{{ versionInfo()?.data?.version ?? 'N/A' }}</p>
      <p class="text-xs text-muted-foreground mt-1">{{ versionInfo()?.data?.environment ?? 'N/A' }}</p>
    </div>
  </div>

  <!-- Details Row -->
  <div class="grid grid-cols-1 gap-4 md:grid-cols-2 mb-6">
    <!-- Database Stats -->
    @if (databaseHealth()?.data?.statistics) {
      <div hlmCard>
        <div class="px-6 pt-6 pb-2">
          <h3 class="text-sm font-semibold">Database Statistics</h3>
        </div>
        <div class="px-6 pb-6">
          <div class="grid grid-cols-3 gap-4 text-center">
            <div>
              <p class="text-2xl font-bold text-primary">{{ databaseHealth()!.data!.statistics.userCount }}</p>
              <p class="text-xs text-muted-foreground">Users</p>
            </div>
            <div>
              <p class="text-2xl font-bold text-green-600">{{ databaseHealth()!.data!.statistics.orderCount }}</p>
              <p class="text-xs text-muted-foreground">Orders</p>
            </div>
            <div>
              <p class="text-2xl font-bold text-blue-600">{{ databaseHealth()!.data!.statistics.parcelCount }}</p>
              <p class="text-xs text-muted-foreground">Parcels</p>
            </div>
          </div>
          @if (databaseHealth()?.data?.message) {
            <p class="text-xs text-muted-foreground mt-3">{{ databaseHealth()!.data!.message }}</p>
          }
        </div>
      </div>
    }

    <!-- System Details -->
    @if (fullHealth()?.data) {
      <div hlmCard>
        <div class="px-6 pt-6 pb-2">
          <h3 class="text-sm font-semibold">System Details</h3>
        </div>
        <div class="px-6 pb-6 space-y-2 text-sm">
          <div class="flex justify-between">
            <span class="text-muted-foreground">Environment</span>
            <span class="font-medium">{{ fullHealth()!.data!.environment }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-muted-foreground">Database</span>
            <span class="font-medium">{{ fullHealth()!.data!.database }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-muted-foreground">Deployment</span>
            <span class="font-medium">{{ fullHealth()!.data!.deploymentMethod ?? 'N/A' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-muted-foreground">Version</span>
            <span class="font-medium">{{ fullHealth()!.data!.version }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-muted-foreground">Timestamp</span>
            <span class="text-xs">{{ fullHealth()!.data!.timestamp | date:'medium' }}</span>
          </div>
        </div>
      </div>
    }
  </div>

  <!-- System Resources + External Services Row -->
  <div class="grid grid-cols-1 gap-4 md:grid-cols-2 mb-6">
    <!-- System Resources -->
    <div hlmCard>
      <div class="px-6 pt-6 pb-2">
        <h3 class="text-sm font-semibold">System Resources</h3>
      </div>
      <div class="px-6 pb-6">
        @if (systemResources() === null) {
          <div class="space-y-2">
            <hlm-skeleton class="h-3 w-full" />
            <hlm-skeleton class="h-3 w-3/4" />
            <hlm-skeleton class="h-3 w-1/2" />
          </div>
        } @else {
          <div class="space-y-3 text-sm">
            <div>
              <div class="flex justify-between mb-1">
                <span class="text-muted-foreground">CPU Usage</span>
                <span class="font-medium">{{ systemResources()!.cpuUsagePercent }}%</span>
              </div>
              <div class="w-full bg-muted rounded-full h-1.5">
                <div class="h-1.5 rounded-full transition-all"
                  [class.bg-green-500]="systemResources()!.cpuUsagePercent < 70"
                  [class.bg-amber-500]="systemResources()!.cpuUsagePercent >= 70 && systemResources()!.cpuUsagePercent < 90"
                  [class.bg-red-500]="systemResources()!.cpuUsagePercent >= 90"
                  [style.width.%]="systemResources()!.cpuUsagePercent">
                </div>
              </div>
            </div>
            <div>
              <div class="flex justify-between mb-1">
                <span class="text-muted-foreground">Memory</span>
                <span class="font-medium">{{ systemResources()!.memoryUsedMb }}MB / {{ systemResources()!.memoryTotalMb }}MB ({{ memoryPercent() }}%)</span>
              </div>
              <div class="w-full bg-muted rounded-full h-1.5">
                <div class="h-1.5 rounded-full transition-all"
                  [class.bg-green-500]="memoryPercent() < 70"
                  [class.bg-amber-500]="memoryPercent() >= 70 && memoryPercent() < 90"
                  [class.bg-red-500]="memoryPercent() >= 90"
                  [style.width.%]="memoryPercent()">
                </div>
              </div>
            </div>
            <div>
              <div class="flex justify-between mb-1">
                <span class="text-muted-foreground">Disk</span>
                <span class="font-medium">{{ systemResources()!.diskUsedGb }}GB / {{ systemResources()!.diskTotalGb }}GB ({{ diskPercent() }}%)</span>
              </div>
              <div class="w-full bg-muted rounded-full h-1.5">
                <div class="h-1.5 rounded-full transition-all"
                  [class.bg-green-500]="diskPercent() < 80"
                  [class.bg-amber-500]="diskPercent() >= 80 && diskPercent() < 95"
                  [class.bg-red-500]="diskPercent() >= 95"
                  [style.width.%]="diskPercent()">
                </div>
              </div>
            </div>
            <div class="flex justify-between">
              <span class="text-muted-foreground">Threads</span>
              <span class="font-medium">{{ systemResources()!.threadCount }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-muted-foreground">Uptime</span>
              <span class="font-medium">{{ formatUptime(systemResources()!.uptimeSeconds) }}</span>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- External Services -->
    <div hlmCard>
      <div class="px-6 pt-6 pb-2">
        <h3 class="text-sm font-semibold">External Services</h3>
      </div>
      <div class="px-6 pb-6">
        @if (externalServices() === null) {
          <div class="space-y-2">
            <hlm-skeleton class="h-8 w-full" />
            <hlm-skeleton class="h-8 w-full" />
          </div>
        } @else {
          <div class="space-y-2">
            @for (svc of externalServices()!.services; track svc.name) {
              <div class="flex items-center justify-between py-2 border-b border-border last:border-0">
                <span class="text-sm font-medium">{{ svc.name }}</span>
                <div class="flex items-center gap-2">
                  @if (svc.responseTimeMs != null) {
                    <span class="text-xs text-muted-foreground">{{ svc.responseTimeMs }}ms</span>
                  }
                  <span hlmBadge [variant]="svc.isReachable ? 'default' : 'destructive'">
                    {{ svc.isReachable ? 'Online' : 'Offline' }}
                  </span>
                </div>
              </div>
            }
            @if (externalServices()!.services.length === 0) {
              <p class="text-sm text-muted-foreground">No external services configured.</p>
            }
          </div>
        }
      </div>
    </div>
  </div>
}

<!-- Application Information -->
@if (versionInfo()?.data) {
  <div hlmCard class="mb-6">
    <div class="px-6 pt-6 pb-2">
      <h3 class="text-sm font-semibold">Application Information</h3>
    </div>
    <div class="px-6 pb-6">
      <div class="grid grid-cols-2 gap-4 md:grid-cols-4 text-sm">
        <div>
          <p class="text-muted-foreground mb-1">Application</p>
          <p class="font-medium">{{ versionInfo()!.data!.application ?? 'N/A' }}</p>
        </div>
        <div>
          <p class="text-muted-foreground mb-1">Version</p>
          <p class="font-medium">{{ versionInfo()!.data!.version ?? 'N/A' }}</p>
        </div>
        <div>
          <p class="text-muted-foreground mb-1">Framework</p>
          <p class="font-medium">{{ versionInfo()!.data!.framework ?? 'N/A' }}</p>
        </div>
        <div>
          <p class="text-muted-foreground mb-1">Machine</p>
          <p class="font-medium">{{ versionInfo()!.data!.machineName ?? 'N/A' }}</p>
        </div>
      </div>
      <div class="flex gap-8 mt-4 text-sm">
        <div><span class="text-muted-foreground">Process ID:</span> <span class="font-medium">{{ versionInfo()!.data!.processId ?? 'N/A' }}</span></div>
        @if (versionInfo()!.data!.deploymentInfo?.autoDeploy) {
          <span hlmBadge variant="default">Auto Deploy</span>
        }
      </div>
    </div>
  </div>
}

<!-- Error Logs Section -->
<div hlmCard>
  <div class="px-6 pt-6 pb-2">
    <div class="flex items-center justify-between">
      <h3 class="text-sm font-semibold">Error Logs &amp; Remote Debugging</h3>
      <div class="flex gap-2">
        <button hlmBtn variant="outline" size="sm" (click)="loadErrorLogs()">Refresh</button>
        <button hlmBtn variant="destructive" size="sm" (click)="clearOldLogs(30)">Clear &gt;30d</button>
        <button hlmBtn variant="destructive" size="sm" (click)="clearOldLogs(90)">Clear &gt;90d</button>
      </div>
    </div>
  </div>
  <div class="px-6 pb-6">

    <!-- Stats Row -->
    @if (statsEntries().length > 0) {
      <div class="grid grid-cols-2 gap-3 md:grid-cols-4 mb-4">
        @for (stat of statsEntries(); track stat[0]) {
          <div hlmCard class="p-3 text-center">
            <p class="text-xs text-muted-foreground mb-1">{{ stat[0] }}</p>
            <p class="text-2xl font-bold"
              [class.text-blue-600]="stat[0] === 'Info'"
              [class.text-amber-600]="stat[0] === 'Warning'"
              [class.text-red-600]="stat[0] === 'Error' || stat[0] === 'Critical'">
              {{ stat[1] }}
            </p>
            <p class="text-xs text-muted-foreground">Last {{ errorLogFilter.sinceHours }}h</p>
          </div>
        }
      </div>
    }

    <!-- Filters -->
    <div class="grid grid-cols-2 gap-3 md:grid-cols-5 mb-4 items-end">
      <div>
        <label hlmLabel class="mb-1 block">Level</label>
        <select class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          [(ngModel)]="errorLogFilter.level">
          <option value="all">All Levels</option>
          <option value="Info">Info</option>
          <option value="Warning">Warning</option>
          <option value="Error">Error</option>
          <option value="Critical">Critical</option>
        </select>
      </div>
      <div>
        <label hlmLabel class="mb-1 block">Source</label>
        <input hlmInput class="w-full" type="text" [(ngModel)]="errorLogFilter.source" placeholder="e.g. MobileScanningController" />
      </div>
      <div>
        <label hlmLabel class="mb-1 block">Time Range</label>
        <select class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          [(ngModel)]="errorLogFilter.sinceHours">
          <option [ngValue]="1">Last 1 hour</option>
          <option [ngValue]="6">Last 6 hours</option>
          <option [ngValue]="24">Last 24 hours</option>
          <option [ngValue]="168">Last 7 days</option>
        </select>
      </div>
      <div>
        <label hlmLabel class="mb-1 block">Count</label>
        <select class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          [(ngModel)]="errorLogFilter.count">
          <option [ngValue]="20">20</option>
          <option [ngValue]="50">50</option>
          <option [ngValue]="100">100</option>
          <option [ngValue]="200">200</option>
        </select>
      </div>
      <div class="flex gap-2">
        <button hlmBtn size="sm" (click)="applyErrorLogFilters()">Apply</button>
        <button hlmBtn variant="outline" size="sm" (click)="resetErrorLogFilters()">Reset</button>
      </div>
    </div>

    <!-- Table -->
    <div class="overflow-auto max-h-[500px] rounded-md border border-border">
      <table hlmTable class="w-full">
        <thead hlmTHead class="sticky top-0 z-10 bg-background">
          <tr hlmTr>
            <th hlmTh class="w-8">#</th>
            <th hlmTh class="w-36">Timestamp</th>
            <th hlmTh class="w-24">Level</th>
            <th hlmTh class="w-52">Source</th>
            <th hlmTh>Message</th>
            <th hlmTh class="w-28">User</th>
            <th hlmTh class="w-16"></th>
          </tr>
        </thead>
        <tbody hlmTBody>
          @if (errorLogsLoading()) {
            @for (_ of [1,2,3,4,5]; track $index) {
              <tr hlmTr>
                <td hlmTd colspan="7" class="py-1">
                  <hlm-skeleton class="h-6 w-full" />
                </td>
              </tr>
            }
          } @else if (errorLogs().length === 0) {
            <tr hlmTr>
              <td hlmTd colspan="7" class="py-8 text-center text-muted-foreground text-sm">
                No error logs found. System is healthy.
              </td>
            </tr>
          } @else {
            @for (log of paginatedErrorLogs(); track log.id; let i = $index) {
              <tr hlmTr
                [class.bg-red-50]="log.level === 'Critical' || log.level === 'Error'"
                [class.bg-amber-50]="log.level === 'Warning'">
                <td hlmTd class="text-xs">{{ (errorLogPage() - 1) * errorLogPageSize + i + 1 }}</td>
                <td hlmTd class="text-xs">{{ log.timestamp | date:'short' }}</td>
                <td hlmTd>
                  <span hlmBadge [variant]="getLevelBadgeVariant(log.level)">{{ log.level }}</span>
                </td>
                <td hlmTd class="text-xs max-w-[200px] truncate" [title]="log.source">{{ log.source }}</td>
                <td hlmTd class="text-xs max-w-xs truncate" [title]="log.message">{{ log.message }}</td>
                <td hlmTd class="text-xs">{{ log.userId ?? 'Unknown' }}</td>
                <td hlmTd>
                  <button hlmBtn variant="outline" size="sm" (click)="showErrorDetails(log)" aria-label="View details">
                    <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                  </button>
                </td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    @if (totalErrorLogPages() > 1) {
      <div class="flex items-center justify-center gap-1 mt-3">
        <button hlmBtn variant="outline" size="sm" [disabled]="errorLogPage() === 1"
          (click)="setPage(errorLogPage() - 1)">Previous</button>
        @for (p of errorLogPageNumbers(); track p) {
          <button hlmBtn [variant]="errorLogPage() === p ? 'default' : 'outline'" size="sm"
            (click)="setPage(p)">{{ p }}</button>
        }
        <button hlmBtn variant="outline" size="sm" [disabled]="errorLogPage() === totalErrorLogPages()"
          (click)="setPage(errorLogPage() + 1)">Next</button>
      </div>
    }
  </div>
</div>

<!-- Error Detail Dialog -->
@if (selectedErrorLog()) {
  <brn-dialog [state]="errorDetailOpen() ? 'open' : 'closed'" (stateChanged)="errorDetailOpen.set($event === 'open')">
    <div hlmDialogOverlay></div>
    <hlm-dialog-content class="max-w-2xl max-h-[85vh] overflow-y-auto">
      <hlm-dialog-header>
        <h3 hlmDialogTitle>Error Log Details</h3>
        <p hlmDialogDescription>
          <span hlmBadge [variant]="getLevelBadgeVariant(selectedErrorLog()!.level)">{{ selectedErrorLog()!.level }}</span>
        </p>
      </hlm-dialog-header>

      <div class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm mt-4">
        <span class="text-muted-foreground font-medium">ID:</span>
        <span>{{ selectedErrorLog()!.id }}</span>

        <span class="text-muted-foreground font-medium">Timestamp:</span>
        <span>{{ selectedErrorLog()!.timestamp | date:'full' }}</span>

        <span class="text-muted-foreground font-medium">Source:</span>
        <code class="text-xs bg-muted px-1 py-0.5 rounded">{{ selectedErrorLog()!.source }}</code>

        <span class="text-muted-foreground font-medium">User:</span>
        <span>{{ selectedErrorLog()!.userId ?? 'Unknown' }}</span>

        <span class="text-muted-foreground font-medium">IP Address:</span>
        <span>{{ selectedErrorLog()!.ipAddress ?? 'N/A' }}</span>

        <span class="text-muted-foreground font-medium">Request:</span>
        <code class="text-xs bg-muted px-1 py-0.5 rounded">{{ selectedErrorLog()!.requestMethod }} {{ selectedErrorLog()!.requestPath }}</code>

        @if (selectedErrorLog()!.errorCode) {
          <span class="text-muted-foreground font-medium">Error Code:</span>
          <code class="text-xs bg-muted px-1 py-0.5 rounded">{{ selectedErrorLog()!.errorCode }}</code>
        }

        @if (selectedErrorLog()!.userAgent) {
          <span class="text-muted-foreground font-medium">User Agent:</span>
          <span class="text-xs break-all">{{ selectedErrorLog()!.userAgent }}</span>
        }
      </div>

      <div hlmCard class="mt-4">
        <div class="px-4 pt-4 pb-2">
          <p class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Error Message</p>
        </div>
        <div class="px-4 pb-4">
          <pre class="text-xs whitespace-pre-wrap break-all">{{ selectedErrorLog()!.message }}</pre>
        </div>
      </div>

      @if (selectedErrorLog()!.stackTrace) {
        <div hlmCard class="mt-3">
          <div class="px-4 pt-4 pb-2">
            <p class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Stack Trace</p>
          </div>
          <div class="px-4 pb-4 max-h-48 overflow-y-auto">
            <pre class="text-xs whitespace-pre-wrap break-all">{{ selectedErrorLog()!.stackTrace }}</pre>
          </div>
        </div>
      }

      @if (selectedErrorLog()!.additionalData) {
        <div hlmCard class="mt-3">
          <div class="px-4 pt-4 pb-2">
            <p class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Additional Data</p>
          </div>
          <div class="px-4 pb-4 max-h-32 overflow-y-auto">
            <pre class="text-xs whitespace-pre-wrap break-all">{{ selectedErrorLog()!.additionalData }}</pre>
          </div>
        </div>
      }

      <hlm-dialog-footer class="mt-4">
        <button hlmBtn variant="outline" (click)="closeErrorDetails()">Close</button>
      </hlm-dialog-footer>
    </hlm-dialog-content>
  </brn-dialog>
}
